// Route: /method — публичная индексируемая страница «Метод».
//
// Роут публичный: `/method` НЕ входит в `PROTECTED_PREFIXES` (`lib/protected-routes.ts`),
// а всё, чего там нет, доступно гостю по умолчанию.
//
// Разметка: `ScholarlyArticle` описывает саму страницу (изложение со ссылками на
// работы), `HowTo` — маршрут темы из трёх шагов. Обе собираются из тех же ключей,
// что рисует страница: отдельных SEO-текстов у них нет, иначе разметка и видимый
// текст разъедутся при первой правке копирайта.

import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { doiUrl, METHOD_SOURCES } from "@/components/public/method/_sources";
import { MethodPage } from "@/components/public/method/method-page";
import { JsonLd } from "@/components/seo/JsonLd";
import { PageMeta } from "@/components/seo/PageMeta";

export const Route = createFileRoute("/method")({
	component: MethodRoutePage,
});

const PAGE_URL = "https://study.iec.exchange/method";
const STEP_KEYS = ["step1", "step2", "step3"] as const;

function MethodRoutePage(): React.ReactElement {
	const { t } = useTranslation("method");
	const { t: tc } = useTranslation("common");

	const articleSchema = useMemo(
		() => ({
			"@type": "ScholarlyArticle",
			headline: t("hero.title"),
			description: t("seo.description"),
			url: PAGE_URL,
			inLanguage: ["ru", "en"],
			isAccessibleForFree: true,
			publisher: { "@type": "Organization", name: "StudyHub" },
			// `citation` — те же одиннадцать работ, что показывает раздел «Источники».
			citation: METHOD_SOURCES.map((source) => ({
				"@type": "ScholarlyArticle",
				name: source.title,
				author: source.authors,
				datePublished: source.year,
				isPartOf: source.reference,
				sameAs: doiUrl(source.doi),
			})),
		}),
		[t],
	);

	const howToSchema = useMemo(
		() => ({
			"@type": "HowTo",
			name: t("overview.title"),
			description: t("overview.lead"),
			step: STEP_KEYS.map((key, i) => ({
				"@type": "HowToStep",
				position: i + 1,
				name: tc(`method.${key}.label`),
				text: t(`overview.${key}`),
				url: `${PAGE_URL}#${key}`,
			})),
		}),
		[t, tc],
	);

	return (
		<>
			<PageMeta
				title={t("seo.title")}
				description={t("seo.description")}
				path="/method"
				og={{ type: "article" }}
				noTitleSuffix
			/>
			<JsonLd id="method-article" schema={articleSchema} />
			<JsonLd id="method-howto" schema={howToSchema} />
			<MethodPage />
		</>
	);
}
